import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useSession } from "@/app/session";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface Report {
  id: string;
  kind: "bug" | "idea";
  message: string;
  expected: string | null;
  page: string | null;
  boardId: string | null;
  version: string | null;
  userAgent: string | null;
  viewport: string | null;
  extra: string | null;
  createdAt: string;
  resolvedAt: string | null;
  userName: string | null;
  userEmail: string | null;
}

/** Owners/admins: every problem report users sent, newest first, with a resolve toggle. */
export default function Reports() {
  const t = useT();
  const { me } = useSession();
  const [items, setItems] = useState<Report[] | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const load = useCallback(async () => setItems(await api<Report[]>("/api/feedback")), []);
  useEffect(() => {
    void load();
  }, [load]);
  if (me && me.staffRole !== "owner" && me.staffRole !== "admin") return null;
  async function resolve(r: Report) {
    await api(`/api/feedback/${r.id}`, { method: "PATCH", json: { resolved: !r.resolvedAt } });
    await load();
  }
  const visible = items?.filter((r) => showResolved || !r.resolvedAt) ?? null;
  return (
    <Shell>
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <h1 className="display text-4xl">.{t.feedback.reports}</h1>
          <label className="ml-auto flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            {t.feedback.showResolved}
          </label>
        </div>
        <p className="mb-4 text-sm text-muted">{t.feedback.reportsHint}</p>
        <ul className="divide-y divide-border">
          {visible?.length === 0 ? <li className="py-2 text-sm text-dim">{t.feedback.none}</li> : null}
          {visible?.map((r) => (
            <li key={r.id} className={`py-3 text-sm ${r.resolvedAt ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-semibold">{r.userName ?? "?"}</span>
                <span className="text-dim">{r.userEmail}</span>
                <span className="text-dim">{new Date(r.createdAt).toLocaleString("es-MX")}</span>
                {r.version ? <span className="text-dim">{r.version}</span> : null}
                <span className="ml-auto flex items-center gap-2">
                  {r.boardId ? (
                    <Link to={`/b/${r.boardId}`} className="text-muted hover:text-text">
                      {t.feedback.openBoard}
                    </Link>
                  ) : null}
                  <Button variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => resolve(r)}>
                    {r.resolvedAt ? t.feedback.reopen : t.feedback.resolve}
                  </Button>
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{r.message}</p>
              {r.expected ? (
                <p className="mt-1 whitespace-pre-wrap text-muted">
                  <span className="font-semibold">{t.feedback.expected}:</span> {r.expected}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => setOpenId(openId === r.id ? null : r.id)}
                className="mt-1 text-xs text-dim hover:text-text"
              >
                {openId === r.id ? "▾" : "▸"} {t.feedback.details}
              </button>
              {openId === r.id ? (
                <pre className="mt-1 overflow-x-auto rounded-sm bg-panel-2 p-2 text-xs text-muted">
                  {[
                    `Page: ${r.page ?? ""}`,
                    `Browser: ${r.userAgent ?? ""}`,
                    `Viewport: ${r.viewport ?? ""}`,
                    r.extra ?? "",
                  ].join("\n")}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
